import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ListMembersService } from '../../../data/member/list-members.service';
import { DatabaseCacheService } from '../../../data/cache/database-cache.service';
import { getJobCodes } from './user-summary-autocomplete-rule';

const STAFF_MEMBER_ROLES = ['STAFF', 'CONTRIBUTOR', 'SUPERVISOR'] as const;

export type StaffOption = { name: string; jobCode: string };

@Injectable({ providedIn: 'root' })
export class UserSummaryFormService {
  private readonly listMembersService = inject(ListMembersService);
  private readonly cache = inject(DatabaseCacheService);

  private staffOptions: StaffOption[] = [];
  private loadingPromise: Promise<StaffOption[]> | null = null;

  async ensureStaffOptionsLoaded(): Promise<StaffOption[]> {
    if (this.staffOptions.length) {
      return this.staffOptions;
    }
    if (this.loadingPromise) {
      return this.loadingPromise;
    }
    const sectionId = this.cache.getSelectedSection()?.sectionId;
    if (!sectionId) {
      this.staffOptions = [];
      return this.staffOptions;
    }
    this.loadingPromise = this.loadStaffOptions(sectionId)
      .then(options => {
        this.staffOptions = options;
        return this.staffOptions;
      })
      .finally(() => {
        this.loadingPromise = null;
      });
    return this.loadingPromise;
  }

  filterStaffOptions(value: string, dataItemName: string): string[] {
    const filterValue = (value ?? '').toLowerCase();
    const jobCodes = getJobCodes(dataItemName);
    return this.staffOptions
      .filter(option =>
        option.name.toLowerCase().includes(filterValue) &&
        (jobCodes.includes('*') || jobCodes.includes(option.jobCode)),
      )
      .map(option => option.name);
  }

  private async loadStaffOptions(sectionId: string): Promise<StaffOption[]> {
    const results = await Promise.all(
      STAFF_MEMBER_ROLES.map(role =>
        firstValueFrom(this.listMembersService.listMembers(sectionId, role, false)),
      ),
    );
    const aggregated: StaffOption[] = [];
    for (const members of results) {
      for (const item of members as any[]) {
        const jobCode = item?.attributes?.jobTitleCode ?? 'OTHER';
        const userName = `${item?.user?.lastName ?? ''} ${item?.user?.firstName ?? ''}`.trim();
        if (!userName) {
          continue;
        }
        if (!aggregated.some(entry => entry.name === userName && entry.jobCode === jobCode)) {
          aggregated.push({ name: userName, jobCode });
        }
      }
    }
    return aggregated.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ja', { sensitivity: 'base' }));
  }
}
